import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Ban, UserPlus, RotateCcw } from "lucide-react";
import {
    adminBetaUsers, adminToggleBeta, adminGrantByEmail, adminResetCosmetics,
} from "@/lib/api";
import { Card, SectionTitle, Spinner, EmptyState, Table, StatusPill, ConfirmDialog } from "./kit";
import MembersSection from "./MembersSection";

const fmtDate = (v) => {
    if (!v) return "—";
    try { return new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return v; }
};

export default function BetaUsersSection() {
    const [rows, setRows] = useState(null);
    const [search, setSearch] = useState("");
    const [grantEmail, setGrantEmail] = useState("");
    const [granting, setGranting] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState(null); // user row
    const [resetTarget, setResetTarget] = useState(null);   // user row

    const load = useCallback(async (q) => {
        try {
            const r = await adminBetaUsers(q);
            setRows(r.users || []);
        } catch { /* auth gate */ }
    }, []);

    useEffect(() => { load(""); }, [load]);

    // Debounced search.
    useEffect(() => {
        const t = setTimeout(() => load(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search, load]);

    const grant = async () => {
        const email = grantEmail.trim();
        if (!email) { toast.error("paste an email first 🍡"); return; }
        setGranting(true);
        try {
            await adminGrantByEmail(email);
            toast.success(`${email} is in the beta now ✨`);
            setGrantEmail("");
            await load(search.trim());
        } catch (e) {
            const msg = e?.response?.data?.detail || "couldn't find an account with that email 🍡";
            toast.error(msg);
        } finally { setGranting(false); }
    };

    const revoke = async () => {
        const row = revokeTarget;
        setRevokeTarget(null);
        try {
            await adminToggleBeta(row.user_id, false);
            toast.success(`beta access removed for ${row.email} 🍡`);
            await load(search.trim());
        } catch { toast.error("couldn't update that user"); }
    };

    const resetCosmetics = async () => {
        const row = resetTarget;
        setResetTarget(null);
        try {
            await adminResetCosmetics(row.user_id);
            toast.success(`${row.display_name || row.email}'s Mochi is back to day one 🌱`);
            await load(search.trim());
        } catch { toast.error("couldn't reset that user's cosmetics"); }
    };

    return (
        <div data-testid="admin-beta-users">
            <SectionTitle hint="Everyone currently in the beta. Grant or revoke access here.">
                Beta users
            </SectionTitle>

            {/* Grant by email */}
            <Card className="p-4 mb-4">
                <label className="block text-warm-800 text-sm font-semibold mb-2">Grant beta to an existing user</label>
                <div className="flex flex-col sm:flex-row gap-2">
                    <input
                        type="email"
                        value={grantEmail}
                        onChange={(e) => setGrantEmail(e.target.value)}
                        placeholder="their@email.com"
                        className="flex-1 px-4 py-2.5 rounded-full bg-surface border border-warm-300/40 text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40"
                        data-testid="grant-email-input"
                    />
                    <button
                        onClick={grant}
                        disabled={granting}
                        className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold text-cream bg-sage hover:bg-sage-hover transition-colors disabled:opacity-60"
                        data-testid="grant-email-btn"
                    >
                        <UserPlus className="w-4 h-4" /> {granting ? "granting…" : "Grant access"}
                    </button>
                </div>
            </Card>

            {/* Search */}
            <div className="relative mb-4">
                <Search className="w-4 h-4 text-warm-500 absolute left-4 top-1/2 -translate-y-1/2" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="search by email…"
                    className="w-full pl-11 pr-4 py-2.5 rounded-full bg-surface border border-warm-300/40 text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40"
                    data-testid="beta-users-search"
                />
            </div>

            {rows === null ? (
                <Spinner label="rounding up the beta crew…" />
            ) : rows.length === 0 ? (
                <Card className="p-2">
                    <EmptyState testid="beta-users-empty">
                        {search ? "no beta users match that search 🍡" : "no beta users yet — invite some friends 🌸"}
                    </EmptyState>
                </Card>
            ) : (
                <Table head={["Email", "Joined", "Via", "Last active", "Done", "Stage", ""]} testid="beta-users-table">
                    {rows.map((u) => (
                        <tr key={u.user_id} className="hover:bg-surface/50 transition-colors" data-testid={`beta-user-row-${u.user_id}`}>
                            <td className="px-4 py-3">
                                <div className="text-warm-900">{u.email || "—"}</div>
                                <div className="text-warm-500 text-xs">{u.display_name}</div>
                            </td>
                            <td className="px-4 py-3 text-warm-500">{fmtDate(u.signup_date)}</td>
                            <td className="px-4 py-3">
                                <span className="text-warm-800 text-xs font-semibold">{u.entry_method}</span>
                            </td>
                            <td className="px-4 py-3 text-warm-500">{u.last_active_date ? fmtDate(u.last_active_date) : "—"}</td>
                            <td className="px-4 py-3 text-warm-800">{u.tasks_completed}</td>
                            <td className="px-4 py-3"><StatusPill status={u.relationship_stage === "old_friend" ? "used" : "pending"} /></td>
                            <td className="px-4 py-3 text-right">
                                <div className="flex items-center gap-1 justify-end">
                                    <button
                                        onClick={() => setResetTarget(u)}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-warm-800 hover:bg-sage/25 transition-colors"
                                        title="Reset cosmetics + relationship clock (keeps all their data)"
                                        data-testid={`beta-user-reset-${u.user_id}`}
                                    >
                                        <RotateCcw className="w-3.5 h-3.5" /> Reset
                                    </button>
                                    <button
                                        onClick={() => setRevokeTarget(u)}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-terracotta hover:bg-terracotta-soft/40 transition-colors"
                                        data-testid={`beta-user-revoke-${u.user_id}`}
                                    >
                                        <Ban className="w-3.5 h-3.5" /> Revoke
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </Table>
            )}

            <ConfirmDialog
                open={!!revokeTarget}
                onClose={() => setRevokeTarget(null)}
                onConfirm={revoke}
                title="Revoke beta access?"
                message={`Are you sure? This will remove beta access for ${revokeTarget?.email || "this user"}. Their tasks, streaks, and Mochi stay exactly as they are — only the beta flag changes 🍡`}
                confirmLabel="Revoke access"
                danger
                testid="beta-user-revoke-confirm"
            />

            <ConfirmDialog
                open={!!resetTarget}
                onClose={() => setResetTarget(null)}
                onConfirm={resetCosmetics}
                title="Reset this tester's Mochi?"
                message={`${resetTarget?.email || "This user"}'s Mochi goes back to day one: sakura pin, crown, hat and sunglasses are un-equipped, milestone celebrations are un-seen, and the relationship restarts at "shy & curious". Tasks, habits, streaks, chat and rice balls are untouched 🌱`}
                confirmLabel="Reset cosmetics"
                danger
                testid="beta-user-reset-confirm"
            />

            <MembersSection />
        </div>
    );
}
